import React from 'react';
import { type DocumentElement as DocumentElementType } from '@eaco/forms-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons/faTriangleExclamation';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons/faCircleInfo';
import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons/faCircleCheck';
import { faCircleXmark } from '@fortawesome/pro-regular-svg-icons/faCircleXmark';
import { faNoteSticky } from '@fortawesome/pro-regular-svg-icons/faNoteSticky';
import HiddenElementBanner from './HiddenElementBanner';
import ArchivedElementBanner from './ArchivedElementBanner';

interface Props {
  element: DocumentElementType;
}

interface Variant {
  bg: string;
  fg: string;
  icon: typeof faTriangleExclamation;
}

// Colour variants map to a soft badge tint + icon colour + default glyph.
const VARIANTS: Record<string, Variant> = {
  warning: { bg: '#fdf1e0', fg: '#c2730c', icon: faTriangleExclamation },
  info: { bg: '#eef4fd', fg: '#2f6fdb', icon: faCircleInfo },
  success: { bg: '#e9f6ef', fg: '#1f8a5b', icon: faCircleCheck },
  danger: { bg: '#fdeef0', fg: '#c0303c', icon: faCircleXmark },
  neutral: { bg: '#f1f3f6', fg: '#5a6472', icon: faNoteSticky },
};

/**
 * Card (Elements Library): a static info/callout container — a variant-tinted
 * icon badge beside a title + description. No value; display-only.
 */
const InfoCard: React.FC<Props> = ({ element }) => {
  const data = element.data as {
    title?: string;
    description?: string;
    colour?: string;
    hidden?: boolean;
  };

  const title = data?.title || 'Card title';
  const description = data?.description || '';
  const variant = VARIANTS[data?.colour || 'warning'] || VARIANTS.neutral;

  return (
    <div className="space-y-2">
      <div className="flex gap-[12px] rounded-[13px] border border-[#e6e9ee] bg-[#fbfcfd] p-[14px]">
        <span
          className="flex h-[38px] w-[38px] flex-none items-center justify-center rounded-[10px]"
          style={{ backgroundColor: variant.bg, color: variant.fg }}
        >
          <FontAwesomeIcon icon={variant.icon} style={{ fontSize: 19 }} />
        </span>
        <div className="flex-1">
          <div className="text-[13.5px] font-bold text-[var(--forms-ink)]">
            {title}
          </div>
          {description && (
            <div className="mt-[2px] text-[12.5px] leading-[1.5] text-[var(--forms-muted)]">
              {description}
            </div>
          )}
        </div>
      </div>
      <HiddenElementBanner hidden={data?.hidden} />
      <ArchivedElementBanner archived={element.archived} />
    </div>
  );
};

export default InfoCard;
